import React from "react";

import {BGAvatar} from "../Avatar/Avatar";
import {MuiIcon} from "../icons/MuiIcon";
import {stringToColor} from "../../../utils/stringToColor";

import "./AssigneeTag.css";

export interface AssigneeTagProps {
    name: string;
    /** A row can be assigned to one specific person or to "any teammate with
     *  this role" — the icon is the only visual cue distinguishing the two. */
    kind: "user" | "role";
    /** Compact variant for contexts where the assignee is secondary to a more
     *  prominent sibling (e.g. a card footer next to a progress score) — same
     *  markup, smaller avatar/icon and de-emphasized name. Defaults to "md". */
    size?: "md" | "sm";
    className?: string;
}

/** Inline avatar/icon + name, used everywhere an audit assignee is displayed
 *  (Assignments tab, Overview's Assignment column) so a user vs. role
 *  assignee always reads the same way. */
export function AssigneeTag({name, kind, size = "md", className}: AssigneeTagProps) {
    const {hue, saturation, lightness} = stringToColor(name);
    // BGAvatar's size prop is a fixed enum (see AvatarSize in Avatar.tsx) whose
    // smallest step is 24px — already this component's default — so the "sm"
    // variant always renders it at 24 and shrinks the rendered avatar visually
    // via AssigneeTag.css instead (see that file's .AssigneeTag--sm rules).
    const classes = ["AssigneeTag", size === "sm" ? "AssigneeTag--sm" : null, className].filter(Boolean).join(" ");
    return (
        <span className={classes}>
            {kind === "role" ? (
                <span className="AssigneeTag-roleIcon">
                    <MuiIcon code="group" size={size === "sm" ? 14 : 16}/>
                </span>
            ) : (
                <BGAvatar
                    size={24}
                    color={`hsl(${hue}, ${saturation}%, ${lightness}%)`}
                    abbreviation={name.trim().charAt(0).toUpperCase()}
                    alt={name}
                />
            )}
            <span className="AssigneeTag-name">{name}</span>
        </span>
    );
}
